// ============================================================================
//  Spin Film 360 — platform-aware viral caption system
// ============================================================================
//  Builds the right share caption per (platform × event_type) so guests
//  don't have to think. Pre-copying the caption to the clipboard before
//  opening TikTok/Instagram/WhatsApp dramatically lifts share-completion
//  rate on platforms whose share APIs don't accept inline caption text.
//
//  Design rules (user brief, May 2026):
//    - Natural, social-native, emotionally framed
//    - Short — NEVER paragraph captions
//    - No corporate boilerplate
//    - No hashtags injected automatically (let guests add their own)
//    - WhatsApp variant trims promo lines — feels personal, not viral
//
//  Templates intentionally hand-tuned per event_type (the dominant
//  context cue). Variations land via small substitutions, not via an
//  LLM — predictable, instant, offline-safe, no token spend.
// ============================================================================

// Event types we recognise — anything else falls through to GENERIC.
// Match the backend's `event_type` slug set: wedding | birthday |
// corporate | kids_party | other (Event model in server.py:977) +
// the `type` field for prom/year6 (server.py:973).
const EVENT_TYPE_TEMPLATES = {
  wedding: {
    hook: (n) => `Caught in cinematic slow-motion at ${n ? `${n}'s wedding` : 'the wedding'} ✨`,
    whatsapp_hook: (n) => `Caught in slow-mo at ${n || 'the wedding'} ✨`,
  },
  prom: {
    hook: () => 'POV: your prom gets turned into a cinematic reel 🎬',
    whatsapp_hook: () => 'POV: prom turned into a cinematic reel 🎬',
  },
  year6: {
    hook: (n) => `Year 6 leavers in slow-motion ✨${n ? ` — ${n}` : ''}`,
    whatsapp_hook: () => 'Caught the leavers moment in slow-mo ✨',
  },
  birthday: {
    hook: () => 'This slow-motion spin is unreal ✨',
    whatsapp_hook: () => 'This slow-mo spin is unreal ✨',
  },
  kids_party: {
    hook: () => 'This slow-mo spin is everything ✨',
    whatsapp_hook: () => 'This slow-mo spin is everything ✨',
  },
  corporate: {
    hook: (n) => `Caught in cinematic slow-motion${n ? ` at ${n}` : ''} ✨`,
    whatsapp_hook: () => 'Caught this in cinematic slow-mo ✨',
  },
};

// Generic fallback — used when event_type is null/unknown OR when
// rendering the standalone /spin (SpinPocket) page that has no event
// context attached.
const GENERIC = {
  hook: () => 'Caught in cinematic slow-motion ✨',
  whatsapp_hook: () => 'Caught this in slow-mo ✨',
};

// Normalize a raw event_type string to one of the template keys. The
// backend stores `event_type` (wedding/birthday/...) AND `type` (prom).
// Year 6 events are flagged via `type === 'year6'` OR `package` slugs;
// the caller is expected to pre-normalise so we just take a single
// slug here.
function pickTemplate(eventType) {
  if (!eventType) return GENERIC;
  const k = String(eventType).trim().toLowerCase();
  return EVENT_TYPE_TEMPLATES[k] || GENERIC;
}

/**
 * Build a platform-aware share caption.
 *
 * @param {Object} args
 * @param {string} args.platform        'tiktok' | 'instagram' | 'whatsapp' | 'generic'
 * @param {string|null} [args.eventType] e.g. 'wedding', 'prom', 'birthday', 'year6'
 * @param {string|null} [args.eventName] human-readable name (e.g. "Emma & James")
 * @param {Object|null} [args.featured] If the spin is currently featured on the
 *                       live wall, the SINGLE proudest category to inject — shape
 *                       matches /api/booth/360/{booth_id}/feature-status:
 *                       { category_slug, category_label, category_icon }.
 *                       Pass null/undefined when the spin is NOT featured —
 *                       the function NEVER fabricates featured copy. This
 *                       authenticity is critical: only earned shares carry it.
 *
 * @returns {string} ready-to-paste caption text. Always multi-line on
 *                   tiktok/instagram (hook + featured? + promo line). WhatsApp
 *                   is single-line emotional only — featured line is dropped
 *                   on WhatsApp because that channel feels personal not viral.
 */
export function buildSpinCaption({ platform, eventType, eventName, featured } = {}) {
  const tpl = pickTemplate(eventType);
  // Defensive trim — event_name may carry trailing whitespace.
  const en = typeof eventName === 'string' ? eventName.trim() : '';

  if (platform === 'whatsapp') {
    // Personal forward — feels like the guest themselves saying it.
    // Single line, no "Made with" promo (preserves the personal feel
    // — the OG preview card already carries the EventDrop brand).
    // Featured line is deliberately omitted on WhatsApp — the channel
    // is forwarded between close contacts and prestige-framing reads
    // as boasting there, not celebration.
    return tpl.whatsapp_hook(en || null);
  }

  // tiktok / instagram / generic copy-caption — emotional hook + the
  // attribution line. We DON'T inject hashtags (user brief) — let the
  // poster add their own venue / event tags.
  const hook = tpl.hook(en || null);
  const promoLine = 'Made with EventDrop Spin Film 360';

  // ── Featured-on-the-live-wall line ──
  // Per the user brief (May 2026): "Emotion first. Recognition second.
  // Brand third." So the featured line lands BETWEEN the hook and the
  // brand attribution. Format is hand-tuned for prestige (NOT gamified):
  //   • Single-category — use the icon + label as a soft headline,
  //     name-decorated for weddings so it reads as celebration:
  //     "🔥 Trending Tonight at Emma & James's wedding"
  //   • Generic / non-wedding — just "🔥 Trending Tonight on the live wall"
  // ALWAYS authentic: only injected when `featured` is non-null. The
  // caller is responsible for passing a truthy value ONLY when the
  // /feature-status endpoint reports `featured: true`.
  let featuredLine = '';
  if (featured && featured.category_label) {
    const icon = featured.category_icon || '✨';
    const label = featured.category_label;
    if (eventType && String(eventType).trim().toLowerCase() === 'wedding' && en) {
      featuredLine = `\n\n${icon} ${label} at ${en}'s wedding`;
    } else {
      featuredLine = `\n\n${icon} ${label} on the live wall`;
    }
  }

  // Only wedding gets the explicit "Try it yourself" CTA per the
  // user's wedding example — the wedding crowd is the highest-LTV
  // booking funnel (couples plan their own next event). For prom /
  // birthday / corporate the brand line alone is enough; over-
  // promoting makes the caption read as an ad.
  if (eventType && String(eventType).trim().toLowerCase() === 'wedding') {
    return `${hook}${featuredLine}\n\n${promoLine}\nTry it yourself: eventdrop.co`;
  }
  return `${hook}${featuredLine}\n\n${promoLine}`;
}

/**
 * Resolve the most useful `event_type` slug from a full event object.
 * The backend has overlapping fields — `event_type` (wedding/birthday/
 * corporate/kids_party/other) AND `type` (prom/year6). Caller usually
 * just wants ONE slug for caption picking; this returns the most
 * specific available.
 */
export function resolveSpinEventType(event) {
  if (!event) return null;
  // `type` is more specific (prom/year6) — prefer it when set.
  if (event.type) return String(event.type).toLowerCase();
  if (event.event_type) return String(event.event_type).toLowerCase();
  return null;
}
